// SB6-ANA-POP-01 — second-monitor placement for the popped-out Viewer.
//
// Christian's decisions (2026-05-27):
//   Q3(b) — auto-pick the non-primary screen when there are ≤2 displays;
//           show a picker when there are ≥3.
//   Q4    — "Do not go true full screen. We always show the Window."
//           So we use the Window Management API only to *place* + *size*
//           an ordinary chrome'd window to fill the target screen's
//           **available** work area. We never call `requestFullscreen()`.
//
// The Window Management API (`window.getScreenDetails()`, `screen.isExtended`,
// the `window-management` permission) is Chromium-only. On Firefox / Safari
// the probe returns `supported:false`; the caller keeps the window on the
// current screen and shows the browser-advice snackbar (see `browserAdvice`).
//
// Placement strategy is **open-then-move**: the caller opens the window
// synchronously inside the click gesture (so the popup blocker is happy and
// the user activation isn't spent on an `await`), then this module's
// `applyPlacement` repositions/resizes the already-open window. `moveTo` /
// `resizeTo` on a window you opened are permitted; with the
// `window-management` permission they may cross monitor boundaries.

/** Minimal shape of a `ScreenDetailed` — only the fields we consume. The
 *  real API exposes more; we keep this narrow so we don't widen lib types. */
interface WMScreenDetailed {
  readonly availLeft: number;
  readonly availTop: number;
  readonly availWidth: number;
  readonly availHeight: number;
  readonly label?: string;
  readonly isPrimary?: boolean;
  readonly isInternal?: boolean;
}

interface WMScreenDetails {
  readonly screens: readonly WMScreenDetailed[];
  readonly currentScreen?: WMScreenDetailed;
}

interface WindowWithManagement {
  getScreenDetails?: () => Promise<WMScreenDetails>;
  screen?: { isExtended?: boolean };
}

/** The public, normalised screen descriptor passed to the picker + placement. */
export interface ScreenInfo {
  readonly availLeft: number;
  readonly availTop: number;
  readonly availWidth: number;
  readonly availHeight: number;
  readonly label: string;
  readonly isPrimary: boolean;
  readonly isInternal: boolean;
}

export type TargetResolution =
  /** Exactly one obvious second screen — place there without asking. */
  | { readonly kind: "auto"; readonly screen: ScreenInfo }
  /** ≥2 candidate screens — caller must let the user choose (Q3b). */
  | { readonly kind: "picker"; readonly screens: readonly ScreenInfo[] }
  /** No targeting — keep the window on the current screen. `reason`
   *  drives the snackbar: `unsupported` → browser advice; the rest are
   *  silent (single monitor) or one-time info (denied). */
  | {
      readonly kind: "none";
      readonly reason: "unsupported" | "single" | "denied" | "error";
    };

type WinLike = (Window & typeof globalThis) | undefined;

function getWin(win?: WinLike): WindowWithManagement | undefined {
  const w = win ?? (typeof window !== "undefined" ? window : undefined);
  return w as unknown as WindowWithManagement | undefined;
}

/** Chromium-only feature probe. Synchronous, no permission prompt. */
export function hasWindowManagement(win?: WinLike): boolean {
  const w = getWin(win);
  return !!w && typeof w.getScreenDetails === "function";
}

/** Cheap "is there a second monitor" probe. No permission prompt. */
export function isExtendedDisplay(win?: WinLike): boolean {
  const w = getWin(win);
  return !!w && w.screen?.isExtended === true;
}

function toScreenInfo(s: WMScreenDetailed, index: number): ScreenInfo {
  return {
    availLeft: s.availLeft,
    availTop: s.availTop,
    availWidth: s.availWidth,
    availHeight: s.availHeight,
    label:
      s.label && s.label.length > 0 ? s.label : `Screen ${String(index + 1)}`,
    isPrimary: s.isPrimary === true,
    isInternal: s.isInternal === true,
  };
}

/**
 * Pure target decision (Q3b). Given the enumerated screens:
 *   - ≤1 screen                → none/single (nothing to target),
 *   - exactly one non-primary  → auto (the 2-monitor case),
 *   - ≥2 non-primary           → picker (the 3+-monitor case).
 * Exported for unit testing without a live API.
 */
export function chooseTarget(screens: readonly ScreenInfo[]): TargetResolution {
  if (screens.length <= 1) return { kind: "none", reason: "single" };
  const nonPrimary = screens.filter((s) => !s.isPrimary);
  const candidates = nonPrimary.length > 0 ? nonPrimary : screens;
  const [first] = candidates;
  if (candidates.length === 1 && first) {
    return { kind: "auto", screen: first };
  }
  return { kind: "picker", screens: candidates };
}

/**
 * Resolve where to place the Viewer window. May trigger the
 * `window-management` permission prompt (first call). Never throws —
 * any failure degrades to `{ kind: 'none', reason }`.
 */
export async function resolveTarget(win?: WinLike): Promise<TargetResolution> {
  const w = getWin(win);
  if (!w || typeof w.getScreenDetails !== "function") {
    return { kind: "none", reason: "unsupported" };
  }
  if (w.screen?.isExtended !== true) {
    return { kind: "none", reason: "single" };
  }
  try {
    const details = await w.getScreenDetails();
    const screens = details.screens.map(toScreenInfo);
    return chooseTarget(screens);
  } catch (err) {
    const name = (err as { name?: string } | undefined)?.name;
    return {
      kind: "none",
      reason: name === "NotAllowedError" ? "denied" : "error",
    };
  }
}

/**
 * Place + size an already-open window onto the target screen, filling its
 * available work area. Windowed — NOT fullscreen (Q4). Best-effort: a
 * cross-origin or permission failure is swallowed (the window simply stays
 * where the browser put it).
 */
export function applyPlacement(child: Window | null, screen: ScreenInfo): void {
  if (!child) return;
  try {
    child.moveTo(screen.availLeft, screen.availTop);
    child.resizeTo(screen.availWidth, screen.availHeight);
  } catch {
    /* swallow — best-effort placement */
  }
}

export interface UseScreenPlacementResult {
  /** True on Chromium with the Window Management API present. */
  readonly supported: boolean;
  /** True when a second display is attached (no prompt). */
  readonly extended: boolean;
  readonly resolveTarget: () => Promise<TargetResolution>;
  readonly applyPlacement: (child: Window | null, screen: ScreenInfo) => void;
}

/** Thin React wrapper binding the helpers to the real `window`. */
export function useScreenPlacement(): UseScreenPlacementResult {
  return {
    supported: hasWindowManagement(),
    extended: isExtendedDisplay(),
    resolveTarget: () => resolveTarget(),
    applyPlacement,
  };
}
